class ViewportVideoSection extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController();
    this.videos = new Map();
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const options = { signal: this.events.signal };

    this.querySelectorAll('video.pps-card__video, video.pit__video, video.pas-media__video, video.pss__video').forEach((video) => {
      const state = { visible: false, manuallyPaused: false, ignorePause: false, pending: false, requested: false, automatic: false };
      this.videos.set(video, state);
      // Playback is started by the observer, never by the HTML autoplay attribute.
      video.autoplay = false;
      if (this.dataset.autoplay !== 'false') {
        video.defaultMuted = true;
        video.muted = true;
      }
      video.addEventListener('play', () => {
        state.automatic = state.requested;
        state.requested = false;
        state.manuallyPaused = false;
        if (!this.isVisible(video, state)) this.pause(video, state);
      }, options);
      video.addEventListener('pause', () => {
        if (state.ignorePause) state.ignorePause = false;
        else if (!video.ended && this.isVisible(video, state)) state.manuallyPaused = true;
        state.automatic = false;
      }, options);
    });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const state = this.videos.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.01;
          this.sync(entry.target, state);
        });
      }, { threshold: 0.01 });
      this.videos.forEach((state, video) => this.observer.observe(video));
    } else {
      this.videos.forEach((state) => { state.visible = true; });
      window.addEventListener('scroll', this.syncAll, { ...options, passive: true });
      window.addEventListener('resize', this.syncAll, options);
      this.syncAll();
    }

    document.addEventListener('visibilitychange', this.syncAll, options);
    window.addEventListener('pagehide', this.pauseAll, options);
    window.addEventListener('pageshow', this.syncAll, options);
    this.motion.addEventListener('change', this.handleMotionChange, options);
  }

  disconnectedCallback() {
    this.events?.abort();
    this.observer?.disconnect();
    this.pauseAll();
    this.videos?.clear();
  }

  isVisible(video, state) {
    if (!this.isConnected || !state.visible || document.visibilityState !== 'visible') return false;
    const bounds = video.getBoundingClientRect();
    return bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight
      && bounds.right > 0 && bounds.left < window.innerWidth;
  }

  pause(video, state) {
    if (video.paused) return;
    state.ignorePause = true;
    video.pause();
  }

  sync(video, state) {
    if (!this.isVisible(video, state)) {
      this.pause(video, state);
      return;
    }
    if (this.dataset.autoplay === 'false' || this.motion.matches || state.manuallyPaused || state.pending || !video.paused) return;
    state.pending = true;
    state.requested = true;
    video.play().catch(() => {
      // Native controls remain available if the browser blocks automatic playback.
      state.requested = false;
    }).finally(() => {
      state.pending = false;
      if (this.videos.get(video) === state && !this.isVisible(video, state)) this.pause(video, state);
    });
  }

  syncAll = () => this.videos?.forEach((state, video) => this.sync(video, state));
  pauseAll = () => this.videos?.forEach((state, video) => this.pause(video, state));

  handleMotionChange = () => {
    this.videos.forEach((state, video) => {
      if (this.motion.matches && state.automatic) this.pause(video, state);
      else this.sync(video, state);
    });
  };
}

if (!customElements.get('premium-process-steps')) {
  customElements.define('premium-process-steps', class extends ViewportVideoSection {});
}

if (!customElements.get('premium-image-text')) {
  customElements.define('premium-image-text', class extends ViewportVideoSection {});
}

if (!customElements.get('premium-about-story')) {
  customElements.define('premium-about-story', class extends ViewportVideoSection {});
}

class SourcingStorySection extends ViewportVideoSection {
  connectedCallback() {
    super.connectedCallback();
    this.track = this.querySelector('.pss__cards');
    this.cards = [...(this.track?.children || [])];
    if (this.cards.length < 2) return;
    this.mobile = matchMedia('(max-width: 749px)');
    this.slideIndex = 0;
    this.userPaused = false;
    this.sliderVisible = false;
    this.sliderFocused = false;
    this.sliderHovered = false;
    const options = { signal: this.events.signal };
    this.querySelector('.pss__carousel-controls')?.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-slide-toggle')) {
        this.userPaused = !this.userPaused;
      } else {
        this.userPaused = true;
        if (button.hasAttribute('data-slide-prev')) this.goToSlide(this.slideIndex - 1);
        if (button.hasAttribute('data-slide-next')) this.goToSlide(this.slideIndex + 1);
        if (button.hasAttribute('data-slide-to')) this.goToSlide(Number(button.dataset.slideTo));
      }
      this.syncSlider();
    }, options);
    this.track.addEventListener('scroll', () => {
      const origin = this.cards[0].offsetLeft;
      this.slideIndex = this.cards.reduce((best, card, index) =>
        Math.abs(card.offsetLeft - origin - this.track.scrollLeft) < Math.abs(this.cards[best].offsetLeft - origin - this.track.scrollLeft) ? index : best, 0);
      this.querySelectorAll('[data-slide-to]').forEach((button, index) => {
        if (index === this.slideIndex) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }, { ...options, passive: true });
    this.track.addEventListener('pointerdown', () => { this.userPaused = true; this.syncSlider(); }, options);
    this.track.addEventListener('keydown', (event) => {
      if (!this.mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.userPaused = true;
      this.goToSlide(event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.slideIndex + (event.key === 'ArrowRight' ? 1 : -1));
      this.syncSlider();
    }, options);
    const carousel = this.querySelector('.pss__carousel');
    carousel.addEventListener('mouseenter', () => { this.sliderHovered = true; this.syncSlider(); }, options);
    carousel.addEventListener('mouseleave', () => { this.sliderHovered = false; this.syncSlider(); }, options);
    carousel.addEventListener('focusin', () => { this.sliderFocused = true; this.syncSlider(); }, options);
    carousel.addEventListener('focusout', (event) => { this.sliderFocused = carousel.contains(event.relatedTarget); this.syncSlider(); }, options);
    this.mobile.addEventListener('change', () => { this.goToSlide(0, false); this.syncSlider(); }, options);
    this.motion.addEventListener('change', this.syncSlider, options);
    document.addEventListener('visibilitychange', this.syncSlider, options);
    if ('IntersectionObserver' in window) {
      this.sliderObserver = new IntersectionObserver(([entry]) => {
        this.sliderVisible = entry.isIntersecting && entry.intersectionRatio >= .25;
        this.syncSlider();
      }, { threshold: .25 });
      this.sliderObserver.observe(this.track);
    }
    this.syncSlider();
  }

  goToSlide(index, smooth = true) {
    this.slideIndex = (index + this.cards.length) % this.cards.length;
    this.track.scrollTo({
      left: this.cards[this.slideIndex].offsetLeft - this.cards[0].offsetLeft,
      behavior: smooth && !this.motion.matches ? 'smooth' : 'instant',
    });
  }

  syncSlider = () => {
    clearInterval(this.sliderTimer);
    const toggle = this.querySelector('[data-slide-toggle]');
    if (toggle) {
      toggle.textContent = this.userPaused ? '▶' : 'Ⅱ';
      toggle.setAttribute('aria-label', this.userPaused ? 'Play slideshow' : 'Pause slideshow');
      toggle.setAttribute('aria-pressed', String(this.userPaused));
    }
    if (!this.mobile?.matches || this.motion.matches || this.userPaused || !this.sliderVisible || this.sliderFocused || this.sliderHovered || document.visibilityState !== 'visible') return;
    this.sliderTimer = setInterval(() => this.goToSlide(this.slideIndex + 1), 5000);
  };

  disconnectedCallback() {
    clearInterval(this.sliderTimer);
    this.sliderObserver?.disconnect();
    super.disconnectedCallback();
  }
}

if (!customElements.get('premium-sourcing-story')) {
  customElements.define('premium-sourcing-story', SourcingStorySection);
}
